import { useTranslations } from '@power-core/toolkit';
import { useRef, useState } from 'react';
import { useAppContext } from '@/modules/app-context/use-app-context';
import { useDocumentsListInit } from '@/modules/document/hooks/use-documents-list-init';
import { useUploadFileByChunks } from '@/modules/project/hooks/use-upload-file-by-chunks';
import { useProjectFileApi } from '@/modules/project-file/hooks/use-project-file-api';
import { useProjectFilesInit } from '@/modules/project-file/hooks/use-project-files-init';
import { useDropdownDateOptions } from '@/modules/utils/custom-hooks/use-dropdown-date-options';
import type { SearchableDropdownItemProps } from '@/ui-components/dropdown/types';
import type { Core } from '@strapi/types';

export const useProjectFilesParams = () => {
	const t = useTranslations();
	const [filters, setFilters] = useState<Core.Helpers.Controllers.GetRequestBody<'api::project-file.project-file', 'getProjectFiles'>>({
		documentName: '',
		documentType: '',
		startDate: '',
		endDate: '',
		projectId: '',
	});
	const { dateOptions } = useDropdownDateOptions();
	const { project, toolsForSelect, showToast } = useAppContext();
	const { isPending: isUploadingPending, uploadProjectAttachment } = useProjectFileApi();
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const { projectDocuments } = useDocumentsListInit({ projectId: project?.documentId });
	const uploadFileByChunks = useUploadFileByChunks();

	const { projectFiles, isLoading, refetch } = useProjectFilesInit({
		projectId: project?.documentId || '',
		documentName: filters.documentName,
		documentType: filters.documentType,
		startDate: filters.startDate,
		endDate: filters.endDate,
	});

	const allProjectFilesSorted = projectFiles?.sort((current, next) => {
		const currentDate = current?.updatedAt ? new Date(current.updatedAt).getTime() : 0;
		const nextDate = next?.updatedAt ? new Date(next.updatedAt).getTime() : 0;
		return nextDate - currentDate;
	});

	const allFilters = projectDocuments?.reduce((acc, cur) => {
		if (cur.name) {
			acc.push({
				value: cur.name,
				label: cur.name,
			});
		}

		return acc;
	}, [] as SearchableDropdownItemProps[]);

	const handleReset = () => {
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
			fileInputRef.current.type = 'text';
			fileInputRef.current.type = 'file';
		}
	};

	const onSuccessFileUpload = () => {
		refetch();
		showToast({ message: t('T_SUCCESS_ATTACHMENT_UPLOADED'), variant: 'success' });
	};

	const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0] || null;

		if (file) {
			await uploadFileByChunks({
				file,
				successCallback: onSuccessFileUpload,
				onReset: handleReset,
				uploadFunction: uploadProjectAttachment,
			});
		}
	};

	const handleButtonClick = () => {
		fileInputRef.current?.click();
	};

	const isPending = isLoading || isUploadingPending;

	return {
		setFilters,
		t,
		dateOptions,
		isPending,
		project,
		fileInputRef,
		allFilters,
		allProjectFilesSorted,
		toolsForSelect,
		onFileChange,
		handleButtonClick,
	};
};
